import { useEffect, useId, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import { FileUp, X } from "lucide-react";
import {
  DndContext,
  DragOverlay,
  MeasuringStrategy,
  type Announcements,
  type ClientRect,
  type DragEndEvent,
  type DragStartEvent,
  type CollisionDetection,
  type DropAnimationFunction,
  type KeyboardCoordinateGetter,
  type UniqueIdentifier,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  type SortingStrategy,
} from "@dnd-kit/sortable";

import { DEFAULT_IMAGE_LIMIT, type UploadedImage } from "@/lib/item-images";
import { useItemImageFailure } from "@/hooks/use-item-image-failure";
import { rearmImageIfAlreadyFailed } from "@/lib/rearm-image";
import { itemPlaceholderMarkZoom } from "@/components/ui/item-image-box-sizes";
import { ItemPlaceholderMark } from "@/components/ui/item-placeholder-mark";
import { MUI_BUTTON_BASE_RESET } from "@/components/ui/button-variants";
import { LinearProgress } from "@/components/ui/linear-progress";
import { MUI_FIELD_HELPER_TEXT } from "@/components/ui/mui-field-shape";
import { cn } from "@/lib/utils";

import {
  DRAG_HANDLE_INSTRUCTIONS,
  DROP_EASING,
  LIFTED_Z_INDEX,
  settleOver,
  SETTLING_Z_INDEX,
  SIBLING_SHIFT,
  SIBLING_TRANSITION,
  useDonorDragBodyRules,
  useDonorDragKeys,
  useDonorSensors,
} from "./dnd-sensors";

// The donor's `MultipleImageUploadField` (`timmy/src/components/fields/…`), the
// control its Request Item dialog ends with and its item edit form reuses —
// shared there, so shared here.
//
// The visual facts, read off the source and carried as values:
//
//   - the label sits above the box as 16px body text, 11px clear of it
//     (`Stack spacing={1.375}`);
//   - the box is a 1px divider-coloured border at 4px radius with 16px of
//     padding, white, tinting to the hover colour while a file is dragged over
//     it *and* on hover (the donor sets both to `lightActionHover.main`);
//   - thumbnails are 99×75 at 4px radius, laid out with 12.8px gutters
//     (`spacing={1.6}`), and the **hero carries a 2px primary border**;
//   - the drop target is 75px tall, grows to the full width when there are no
//     images yet, and stacks a circular icon over its two lines of text — flush,
//     no gap — with "Click to upload or drag and drop images" while empty and
//     "Add Images" once something is in, both 16px at weight 500, then "Allowed
//     file types: .jpg, .png" at 12px, also weight 500, in the secondary ink;
//   - a linear progress bar appears under the row once bytes have actually
//     moved (`progress > 0`) and flips from a measurement to a sliding
//     animation the moment the last byte is sent.
//
// **The hero is the lowest `order_index`, which here is the first image.** The
// donor keeps an `is_hero` flag beside `order_index`, painting the border from
// the row's own flag and rewriting it only on drag-end; ADR-0005 decision 9
// drops the column and rules the semantics — "hero = lowest" — so position is
// the only thing that carries it in Atlas.
//
// **The observable behaviour is not identical** (#411): because the donor never
// rewrites `is_hero` on remove or on submit, removing the first of three images
// leaves *no* tile marked in modsy, with `order_index` stale at 2 and 3 — and a
// subsequent add produces a duplicate index. Atlas always marks exactly one.
// Reintroducing the flag on ticket authority would contradict the ADR, and the
// owner ruled that it stands: the ADR supersedes that acceptance criterion
// rather than meeting it (#411's rulings record).
//
// **Reordering is a `@dnd-kit` sortable plus explicit move controls** (#378).
// The donor uses `react-beautiful-dnd`, which is unmaintained and React-19-
// hostile; the *gesture* is what ports, not the library, and every value below
// was measured on the running donor rather than inferred from its source.
//
// The gesture, as modsy performs it: the tile itself lifts under the pointer at
// full opacity and `z-index: 5000`, its border box frozen; the slot it left
// becomes a gap of exactly that size; every tile between the gap and the
// pointer slides aside over **200ms `cubic-bezier(0.2, 0, 0, 1)`**; the drop
// settles over **330ms `cubic-bezier(0.2, 1, 0.1, 1)`** (rbd scales this with
// distance up to 550ms — a six-tile strip never travels far enough to leave the
// floor) at `z-index: 4500`; a cancel returns home over **0.6×** that. Nothing
// is written unless the release lands on a real position.
//
// **Two ruled divergences in the gesture itself** (#378, PB-1 and PB-4). The
// grab hand is the donor's own rule applied from first paint: rbd declares
// `cursor: grab` on every enabled handle, but writes it into a stylesheet it
// only populates once a drag has happened, so a cold modsy page shows a plain
// arrow over a strip that already reorders — which makes PB-1, as shipped,
// the donor's cursor rules minus a lazy-initialisation bug (`grabbing` in
// flight is plain parity, not part of the break). And the announcements name
// the image — "You have lifted front.jpg in position 1" where the donor says
// "an item", because its tiles carry no accessible name at all and a strip of
// four identical readings is the state that made naming this field's other
// controls worth doing. The tile's own `aria-label` is a third, separate
// question, ruled in #378's decision round: kept, as an accessibility
// improvement.
//
// **The move buttons are additional, and the reason recorded here was wrong.**
// It used to say the donor's drag handle "is unreachable from a keyboard". It
// is reachable: every donor tile is `tabIndex=0 role=button`, Space lifts,
// arrows move, Space drops, Escape cancels — driven on the running donor to
// check. The buttons stay because owner ruling D5 put them there, and a second,
// simpler keyboard route costs nothing; they are not a substitute for a
// keyboard drag, which this control now also has.
//
// **The drop target takes a focus ring the donor's does not.** `ButtonBase`
// sets `outline: 0` and leaves `focusRipple` off (`ButtonBase.js:56,105`), so
// the donor's target shows a keyboard user nothing. Atlas's plain buttons keep
// the browser's own ring, recoloured by the app-wide `outline-ring/50`
// (`index.css:534-537`) — that rule sets a colour, not a ring of its own. Its ✕
// is a different case and not a divergence: `IconButton` passes `focusRipple`
// and `centerRipple` (`IconButton.js:215-217`), so the donor does mark that one
// on focus, with a ripple where Atlas shows an outline. The target's ring is
// additive, and a divergence all the same — named, and then **ruled**: it is
// one of the five owner-licensed breaks on #411's rulings record.

/**
 * The types the donor's uploader accepts, and the words it uses for them.
 *
 * Module-private: they describe *this* control, and the two suites that check
 * the copy assert the sentences rather than importing them — a constant compared
 * against itself proves nothing.
 */
const DONOR_IMAGE_TYPES = ["image/jpeg", "image/png"] as const;
const DONOR_IMAGE_TYPES_HINT = "Allowed file types: .jpg, .png";
const DONOR_WRONG_TYPE_MESSAGE = "Please upload images in .jpg or .png formats only.";

/**
 * What to call a row that has no name.
 *
 * Migrated catalog rows can carry an empty `file_name` — real data, seen on the
 * item edit page (#300). The donor only ever puts the name in an `alt`, so an
 * empty one is invisible there; this field also *labels its controls* with it,
 * because the move and remove buttons are the keyboard route the donor's
 * pointer-only drag handle never had. Unnamed, those labels collapse to "Remove
 * " on every row at once — identical to each other, and useless to a screen
 * reader. The position is the one thing every row has, so it stands in.
 */
const nameOf = (fileName: string, index: number) =>
  fileName.trim() === "" ? `Image ${index + 1}` : fileName;

/** The donor's 99x75 thumbnail frame, which its `ImageCard` fills. */
const UPLOAD_THUMBNAIL = { width: 99, height: 75 } as const;

/**
 * One row's picture, or the donor's generic "no picture" mark when it fails.
 *
 * **Its own component so the failure verdict is per row.** A hook inside the
 * row `.map()` would key hook order to the data, so a removed row would hand
 * its verdict to whichever row shifted into its index.
 *
 * Saved rows point at real objects in the bucket (`item-edit-page.tsx`'s
 * `savedImageRow`, via `itemImageThumbnailUrl`), and a missing one is a real
 * failure here as it is anywhere else in Atlas — without this arm the frame drew
 * Chromium's broken-image glyph with the item name clipped behind it, the same
 * defect #496 reported at the variant tiles. Found by that ticket's
 * cross-review, which was right that the fix had to reach every slot an item
 * image can fail at, not only the strips.
 *
 * **The case that used to escape it, and how it is caught now.** The item edit
 * form is server-rendered, so the browser can finish failing this fetch before
 * React hydrates and attaches `onError` — the `error` event does not replay, so
 * the verdict was simply lost and the glyph stayed. Measured on the running
 * stack (#496 cross-review): a cold load left the glyph up, and nudging the
 * same element into a post-hydration fetch flipped it to the mark immediately.
 * `rearmImageIfAlreadyFailed` is that nudge, wired as this `<img>`'s `ref`
 * (#539). The Add Variant tile needed the same wiring and instead moved onto
 * `ItemPicture`, which leaves this bare `<img>` as the one item-image slot in
 * the app that carries the arm itself rather than inheriting it.
 *
 * It re-runs the fetch and states no verdict, which is what a mount-time
 * `complete && naturalWidth === 0` check could not do. That check looks like
 * the fix and is not, for two reasons: a *freshly created* `<img>` reports the
 * same `complete: true, naturalWidth: 0` before its fetch starts, so the
 * reading cannot tell "failed" from "not begun"; and on a settled element it
 * can still describe the *previous* attempt, re-asserting a stale verdict and
 * breaking the retry this hook exists to allow
 * (`item-image.browser.test.tsx`'s legacy-row retry catches it).
 *
 * Modsy needs no arm here either, and for the same reason: its thumbnail points
 * at the resize CDN, which answers a missing object with a 403 whose body is a
 * decodable placeholder PNG, so the `<img>` loads.
 *
 * The crop is derived for *this* frame and this frame's fit. The donor's
 * `ImageCard` uses `object-fit: cover` (`image-card.tsx:52-60`), not the
 * variant tile's `contain`, and reading it as `contain` draws the mark at 53px
 * where modsy draws 70px — caught by #496's cross-review. Under `cover` it
 * comes out at the same 590% the tiles use, because modsy's canvas is square
 * enough that both fits are width-driven; `itemPlaceholderMarkZoom` carries the
 * reasoning, and asking it is what keeps that from being a coincidence a reader
 * has to trust.
 */
function UploadedImagePicture({ previewUrl, name }: { previewUrl: string; name: string }) {
  // A bare URL rather than a rendition bundle: this control is handed one
  // already-chosen thumbnail, not a ladder to pick from. Keyed on it all the
  // same, so a row pointed at different bytes retries.
  const [failed, noteFailed] = useItemImageFailure({ src: previewUrl, sources: [] });

  // **Only a stored object can be missing.** A row the user has just picked
  // previews from `URL.createObjectURL(file)` — the bytes are in the page, not
  // in the bucket — so a failure there is not "this image is gone" and drawing
  // "no picture" over a file someone is watching upload would be a lie. Saved
  // rows preview from an app URL (`itemImageThumbnailUrl`), and those are the
  // ones that 404.
  const isStoredObject = !previewUrl.startsWith("blob:") && !previewUrl.startsWith("data:");

  if (failed && isStoredObject) {
    return (
      <ItemPlaceholderMark
        className="size-full rounded-[4px] border border-black/20"
        zoom={itemPlaceholderMarkZoom(UPLOAD_THUMBNAIL.width, UPLOAD_THUMBNAIL.height, "cover")}
      />
    );
  }

  return (
    <img
      // Passed bare, for the reason `@/lib/rearm-image` records.
      ref={rearmImageIfAlreadyFailed}
      src={previewUrl}
      alt={name}
      className="size-full rounded-[4px] border border-black/20 object-cover"
      onError={noteFailed}
    />
  );
}

/**
 * Left and right move the lifted tile; up and down do nothing.
 *
 * The donor's list is `direction="horizontal"` with no second droppable beside
 * it, so rbd's cross-axis keys have nowhere to go and ↑/↓ are inert — confirmed
 * live on item 2391. `sortableKeyboardCoordinates` is geometric rather than
 * axis-aware: once this strip wraps past the box's width it would happily walk
 * a tile onto the row below, which modsy cannot do. Gating on the code keeps
 * the wrapped strip reading like the donor's.
 */
/** `useSyncExternalStore` wants a subscription; `<body>` never changes. */
const subscribeToNothing = () => () => {};

const horizontalOnlyCoordinates: KeyboardCoordinateGetter = (event, args) =>
  event.code === "ArrowLeft" || event.code === "ArrowRight"
    ? sortableKeyboardCoordinates(event, args)
    : undefined;

/**
 * `@dnd-kit`'s four announcement callbacks, all saying nothing.
 *
 * Returning `undefined` is how its `announce` is told to leave the region
 * alone (`accessibility.esm.js`'s `useAnnouncement`), so its live region
 * mounts and stays empty while the field speaks the donor's four sentences
 * through one of its own — which is the only way to hold the drop sentence
 * back until the tile has landed. See `announcement` in `ImageUploadField`.
 */
const SAID_ELSEWHERE: Announcements = {
  onDragStart: () => undefined,
  onDragOver: () => undefined,
  onDragEnd: () => undefined,
  onDragCancel: () => undefined,
};

/**
 * The gutter between two tiles on the same row, read off the measured rects.
 *
 * Taken from the strip rather than from its `gap-[12.8px]` class so the two
 * cannot drift apart, and row-aware because the strip wraps: consecutive
 * rects that straddle a line break are a row apart, not a gutter apart.
 */
function gutterBetween(rects: ClientRect[]) {
  for (let i = 1; i < rects.length; i += 1) {
    const before = rects[i - 1];
    const after = rects[i];
    if (before && after && before.top === after.top) {
      return after.left - (before.left + before.width);
    }
  }
  return 0;
}

/**
 * The two distances the strip's own arrangement is built out of: the gutter
 * between tiles, and how far the row closes up behind the tile that has just
 * left the flow — the lifted tile's own width on top of that gutter.
 *
 * Not the distance between two tiles: the first one carries the hero border
 * and so is 4px wider than the rest, which makes the strip's pitch
 * non-uniform. It is the *lifted* tile's width that the gap is worth.
 *
 * Read off the strip itself for the same reason `gutterBetween` is, and
 * row-aware for the same reason too. Only ever asked at the lift, while the
 * row is still at rest: mid-drag the tiles carry transforms of their own.
 */
function measureAtLift(strip: HTMLElement | null, liftedIndex: number) {
  const none = { gutter: 0, collapse: 0 };
  const tiles = strip ? Array.from(strip.children) : [];
  const lifted = tiles[liftedIndex]?.getBoundingClientRect();
  if (!lifted) return none;
  for (let i = 1; i < tiles.length; i += 1) {
    const before = tiles[i - 1]?.getBoundingClientRect();
    const after = tiles[i]?.getBoundingClientRect();
    if (before && after && before.top === after.top) {
      const gutter = after.left - before.right;
      return { gutter, collapse: gutter + lifted.width };
    }
  }
  return none;
}

/**
 * Where every tile sits while one of them is in the air.
 *
 * Two facts about the donor make this neither `horizontalListSortingStrategy`
 * nor a tweak of it, and **reading only the first of them is what made the
 * first attempt at this worse than the strategy it replaced** — so both are
 * written down:
 *
 *  1. rbd displaces a tile the drag has passed by `client.marginBox.width`
 *     (`get-dimension.js:30-33`), and MUI v9's Grid spells the 12.8px gutter
 *     as a flex `gap` on the container rather than as margins on the items
 *     (`@mui/system/Grid/gridGenerator.js:163`). So the displacement is the
 *     bare 99px tile, where `horizontalListSortingStrategy` shifts by the
 *     whole 111.8px pitch.
 *  2. rbd also takes the lifted tile *out of the flex flow* — `position:
 *     fixed` (`get-style.js:67-73`) — and its `Draggable` renders nothing in
 *     the slot it leaves (`draggable.jsx:167`); the placeholder holding the
 *     row's width is appended at the **tail**, as the droppable's last child
 *     (`MultipleImageUploadField.tsx:264-266`). So the slot collapses by the
 *     full pitch before that 99px is added back.
 *
 * Net, in the donor: every tile after the lifted one sits one gutter left of
 * its resting position for the whole drag, and a tile the drag has passed
 * ends up a full pitch left — landing exactly on the vacated slot. Measured
 * on modsy with four tiles and the second lifted: 25 / — / 236 / 347, against
 * resting 25 / 136.8 / 248.6 / 360.4.
 *
 * Atlas keeps the lifted tile in the row at `opacity: 0`, so its slot and
 * *both* its gutters stay reserved and nothing collapses. Fact 1 alone,
 * applied to that reserved layout, put every displaced tile 12.8px right of
 * the donor's and opened a 25.6px double gutter beside the vacated slot that
 * the donor never shows. The collapse has to be spelled out as well, which is
 * the `-gutter` below.
 */
/**
 * A tile's drag identity: **the row, not the image id and not the slot.**
 *
 * The donor gets away with the slot — its `draggableId` is
 * `` `image-${index}` `` (`MultipleImageUploadField.tsx:153`) — because rbd
 * reconciles a drag by index and modsy's rows have no id that changes. Atlas
 * can use neither of those two obvious names:
 *
 *   - **not `image.id`**, because a picked file's row draws before its bytes
 *     are up under a placeholder id, and the upload rewrites that id in place
 *     when the PUT lands (`item-images-uploader.ts`). A drag holding the row
 *     by its id loses the tile in the air the moment its own upload finishes;
 *   - **not the index**, because a second tile can be lifted while the first
 *     is still settling, and `startDrag` pays the first drop before the new
 *     drag reads the list — which moves every row the first drop moved. An
 *     index captured at the press would then name a different image.
 *
 * `UploadedImage.key` is assigned once, when the row appears, and survives
 * both. It is the React key too, for the same reason.
 */
const dragIdOf = (image: UploadedImage) => image.key;

/** One line of the wrapped strip, and the tiles standing in it. */
type TileRow = { top: number; bottom: number; tiles: Array<{ index: number; rect: ClientRect }> };

/**
 * The strip's resting tiles, cut into the rows they are laid out in.
 *
 * **Why the drag needs this at all — owner ruling D7 for #378, 2026-09-22.**
 * rbd's reorder arithmetic is single-axis: `patch(axis.line, displacement)`
 * on a `direction='horizontal'` list writes x and leaves y alone
 * (`get-displaced-by.js:15`, `axis.js:16-25`), and its scan walks the whole
 * home list in index order. modsy never notices, because its item form caps
 * the strip at three images and it never wraps — so the row and the list are
 * the same thing there, and the donor's behaviour for a second row is not a
 * behaviour, it is an absence.
 *
 * Atlas caps at six and wraps at four, so the absence has to be filled.
 * Running the donor's scan unchanged reads a second-row drop against the
 * *first* row — the first tile whose centre the x-test clears wins, and on a
 * wrapped strip that is always a tile the pointer is nowhere near. The ruling
 * is that the release point picks its row first and the edges are then
 * compared only inside it, which is exactly rbd's rule applied to the run of
 * tiles the pointer is actually over.
 *
 * The rects have to be the resting ones — hence the explicit `measuring` on
 * the context — because a row is read off the tops, and a tile carrying a
 * mid-drag transform would have moved.
 */
function rowsOf(images: readonly UploadedImage[], rects: Map<UniqueIdentifier, ClientRect>) {
  const rows: TileRow[] = [];
  for (const [index, image] of images.entries()) {
    const rect = rects.get(dragIdOf(image));
    if (!rect) continue;
    // Flex wrap lays the tiles out in index order, so a row breaks exactly
    // where the top jumps. Half a tile's height is the tolerance: far wider
    // than the subpixel drift between equal-height siblings, and far
    // narrower than the pitch between two rows.
    const open = rows.at(-1);
    if (open && Math.abs(rect.top - open.top) < rect.height / 2) {
      open.bottom = Math.max(open.bottom, rect.bottom);
      open.tiles.push({ index, rect });
    } else {
      rows.push({ top: rect.top, bottom: rect.bottom, tiles: [{ index, rect }] });
    }
  }
  return rows;
}

const donorDisplacement: SortingStrategy = ({
  rects,
  activeNodeRect: fallback,
  activeIndex,
  overIndex,
  index,
}) => {
  const lifted = rects[activeIndex] ?? fallback;
  if (!lifted) return null;

  // **Carried off the strip is not answerable here.** `SortableContext`
  // spells "no destination" as an `overIndex` of -1, and `useSortable` skips
  // the strategy entirely once it sees one (`sortable.esm.js:507,511`) — so
  // a branch on it below would be dead code. The donor's answer for that
  // state is the `stranded` collapse in `ImageUploadField`.

  // The lifted tile's own slot. Invisible — the strip's copy is transparent
  // for the length of the drag — but `@dnd-kit` asks for it all the same, and
  // it is what the drop settles onto, so it is the landing slot in the
  // *contracted* row: the tile the drag has passed, trailing or leading edge
  // depending on which way it went.
  if (index === activeIndex) {
    const landing = rects[overIndex];
    if (!landing) return null;
    return {
      x:
        activeIndex < overIndex
          ? landing.left + landing.width - (lifted.left + lifted.width)
          : landing.left - lifted.left,
      y: 0,
      scaleX: 1,
      scaleY: 1,
    };
  }

  // Behind the lifted tile: nothing collapses ahead of it, so the only
  // movement is getting out of the way once the drag has come back past.
  if (index < activeIndex) {
    return { x: index >= overIndex ? lifted.width : 0, y: 0, scaleX: 1, scaleY: 1 };
  }

  // Ahead of it: the collapse applies for the whole drag, the displacement
  // only once the drag has passed. Together they are the 111.8px pitch.
  return {
    x: -gutterBetween(rects) - (index <= overIndex ? lifted.width : 0),
    y: 0,
    scaleX: 1,
    scaleY: 1,
  };
};

/**
 * The tile's frame: the same classes whether it is sitting in the strip or
 * flying under the pointer, so the two cannot drift apart.
 *
 * The border is on the frame rather than the picture, and the hero's 2px grows
 * the tile to 103×79 among 99×75 siblings — the donor's arithmetic, kept
 * deliberately (#300).
 */
const thumbnailFrame = (hero: boolean) =>
  cn(
    "group/image relative m-0 shrink-0 rounded-[4px]",
    // rbd's three permanent drag-handle rules, the ones it writes into the
    // `always` sheet rather than a state's (`get-styles.js:82-86`): no
    // long-press callout, no tap highlight, and `manipulation` to drop the
    // double-tap zoom delay. They are handle *styles* rather than sensor
    // rules — the sensor's half of the touch port is `DonorPointerSensor`,
    // ruling D4 — and without them an Atlas thumbnail flashes grey and pops
    // a callout on a touch device where a modsy one does neither.
    "touch-manipulation [-webkit-tap-highlight-color:rgba(0,0,0,0)] [-webkit-touch-callout:none]",
    hero ? "border-primary border-2" : "border-none",
  );

/**
 * What a tile shows. Rendered twice while a drag is in flight — once in the
 * strip (hidden, holding the gap) and once in the overlay (the tile the editor
 * is dragging) — which is why it is a component rather than inline markup.
 */
function ThumbnailBody({
  image,
  index,
  name,
  total,
  disabled,
  onRemove,
  onMove,
}: {
  image: UploadedImage;
  index: number;
  name: string;
  total: number;
  disabled: boolean;
  onRemove: (index: number) => void;
  onMove: (from: number, to: number) => void;
}) {
  // A press on the ✕ or the ‹ › never lifts the tile: the shared sensor
  // refuses a press inside a button, as rbd's `tryGetLock` does
  // (`isPressOnInteractiveElement`, `dnd-sensors.ts`).

  return (
    <>
      {/* The `ImageCard` inside the donor's bordered wrapper: `sx={{ width:
      99, height: 75 }}`, and the frame the overlay is positioned in. */}
      <div className="relative h-[75px] w-[99px] overflow-hidden rounded-[4px]">
        {/* `border: '1px solid rgba(0, 0, 0, 0.2)'` with `borderRadius:
      '4px'` on the donor's own `img` (`image-card.tsx:55-62`) — what
      keeps a pale thumbnail from bleeding into the white box. */}
        <UploadedImagePicture previewUrl={image.previewUrl} name={name} />
        <figcaption className="sr-only">{index === 0 ? `${name} (main image)` : name}</figcaption>
        <div className="absolute inset-x-0 bottom-0 flex justify-between bg-black/45 opacity-0 transition-opacity group-focus-within/image:opacity-100 group-hover/image:opacity-100">
          <button
            type="button"
            disabled={disabled || index === 0}
            aria-label={`Move ${name} earlier`}
            onClick={() => onMove(index, index - 1)}
            className="px-1 text-xs text-white disabled:opacity-40"
          >
            ‹
          </button>
          <button
            type="button"
            disabled={disabled || index === total - 1}
            aria-label={`Move ${name} later`}
            onClick={() => onMove(index, index + 1)}
            className="px-1 text-xs text-white disabled:opacity-40"
          >
            ›
          </button>
        </div>
      </div>

      {/* **Always on screen, never on hover.** The donor renders its
      delete unconditionally — a black circular `CloseIcon` overhanging
      the tile's top-right at `right: -12px, top: -10px`, `#2c2c2c` on
      hover (`image-card.tsx:7-31,64`). Atlas had it inside the hover
      bar with the move controls, so a saved image showed no way to
      remove it until a pointer arrived, and none at all on a touch
      screen. Restored on owner ruling D1 for #300. The move buttons
      stay in the bar: they are an Atlas addition (ruling D5) and the
      donor has no second control here for them to sit beside. */}
      <button
        type="button"
        // **Frozen with the rest of the field while a save is in the
        // air — a named parity break on owner ruling D8 for #411.**
        // The donor never disables this button: its `disabled`
        // reaches the `FormControl` and the drop-target `ButtonBase`
        // and nothing else (`:41,48,187`), so modsy lets a row be
        // taken away mid-submit. It also sends the image list it
        // captured when submit was pressed, so that removal is not
        // in the payload: the screen reports success with the image
        // still attached. The ruling is that an edit taken after the
        // save has left is the wrong thing to accept, so the control
        // goes inert for the length of the request rather than
        // lying. Removal at rest is untouched — `disabled` is the
        // save, not the uploads (ruling D2 keeps the ✕ live while
        // bytes are moving, which is what makes it touch-reachable).
        disabled={disabled}
        aria-label={`Remove ${name}`}
        onClick={() => onRemove(index)}
        // 24px overall: a 20px `CloseIcon fontSize='small'`
        // (`pxToRem(20)`, `SvgIcon.js:68-71`) inside 2px of padding, and
        // the ground crosses to #2c2c2c over `duration.shortest` =
        // 150ms, the `IconButton` default (`IconButton.js:60-62`).
        className={cn(
          "hover:bg-lennar-delete-hover absolute -top-[10px] -right-[12px] z-10 flex size-6 items-center justify-center rounded-full bg-black p-[2px] text-white transition-colors duration-150 ease-[cubic-bezier(0.4,0,0.2,1)]",
          // `IconButton` is `styled(ButtonBase)` (`IconButton.js:42`),
          // so the donor's ✕ carries the same reset.
          MUI_BUTTON_BASE_RESET,
        )}
      >
        <X className="size-5" />
      </button>
    </>
  );
}

/**
 * One tile in the strip, wired as a `@dnd-kit` sortable.
 *
 * **The whole tile is the handle**, as in the donor, which spreads
 * `draggableProps` and `dragHandleProps` onto the same wrapper — there is no
 * grip icon and no separate handle region.
 *
 * While a drag is in flight the tile stays in the flex row and goes
 * transparent: it is the gap. The tile the editor sees is the overlay's copy.
 * `pointer-events: none` goes on **every** tile for the length of the drag, not
 * just this one — rbd does the same to every handle (`get-styles.js:88`), which
 * is what keeps the ✕ and the ‹ › of a tile you drag *past* from reacting.
 */
function SortableThumbnail({
  image,
  index,
  name,
  total,
  disabled,
  dragEnabled,
  dragInFlight,
  cancelSettling,
  strandedBy,
  heldAt,
  heldOver,
  heldHidden,
  onRemove,
  onMove,
}: {
  image: UploadedImage;
  index: number;
  name: string;
  total: number;
  disabled: boolean;
  dragEnabled: boolean;
  dragInFlight: boolean;
  cancelSettling: boolean;
  /**
   * How far this tile has closed up because the drag is over no droppable at
   * all — 0 whenever the strategy has an answer of its own. See `stranded`
   * in `ImageUploadField`, which is where the number comes from and why.
   */
  strandedBy: number;
  /**
   * Where this tile stands for the length of the settle, while the drop
   * animation runs and before the reorder lands. See `held` in
   * `ImageUploadField`. Absent whenever no tile is settling.
   */
  heldAt: number | undefined;
  /** The curve it takes to get there, or none when it is already in place. */
  heldOver: string | undefined;
  /** True for the tile in the air: its slot stays empty until it arrives. */
  heldHidden: boolean;
  onRemove: (index: number) => void;
  onMove: (from: number, to: number) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: dragIdOf(image),
    disabled: !dragEnabled,
    transition: SIBLING_SHIFT,
  });

  return (
    <figure
      ref={setNodeRef}
      // **A named parity break, ruled by the app owner in session on
      // 2026-09-21: keep it, because it is an accessibility improvement.**
      //
      // The donor's tiles are unlabelled buttons — `alt=''` on the picture
      // and no label on the handle (`image-card.tsx:53`, `draggable.jsx:105-
      // 113`) — so a screen reader there hears "button" and a position.
      // Atlas's tile has named children where the donor's has none (the ✕ and
      // the ‹ ›, rulings D1 and D5 for #300), so dropping this label does not
      // reproduce modsy's silence: it replaces one name with the tile's whole
      // subtree read out. This is *not* PB-4 — that ruling is about the
      // announcements naming the image, and an earlier revision of this
      // comment claimed the two were the same break when the ticket says no
      // such thing. The same `nameOf` stands in for a migrated row whose
      // `file_name` is empty.
      aria-label={name}
      // **`attributes` unconditionally; only `listeners` are gated.** Every
      // donor tile carries `tabIndex: 0`, `role: 'button'` and
      // `aria-describedby` whatever the state — `isEnabled` is always true
      // there, because `MultipleImageUploadField.tsx:154` never passes
      // `isDragDisabled`. PB-2 (one image) and PB-3 (mid-save) rule that the
      // *drag* goes inert; neither says the tile leaves the tab order or
      // stops announcing as a button, and gating both spreads together took
      // it out of reach of the keyboard entirely on a one-image strip.
      {...attributes}
      // **Except the instructions, while the drag is inert — owner ruling
      // D3 for #378, 2026-09-22.** The description `@dnd-kit` points at is
      // rbd's own sentence, "press space bar to start a drag", and in the
      // two states Atlas adds — a one-image strip (PB-2) and a save in
      // flight (PB-3) — space bar does nothing. The donor never reaches
      // either state, so there is no modsy behaviour this contradicts; the
      // ruling is that telling someone to perform a gesture that will not
      // happen is worse than the small divergence from
      // `draggable.jsx:105-113`. The tab stop and the button role stay, so
      // nothing leaves the keyboard's reach.
      aria-describedby={dragEnabled ? attributes["aria-describedby"] : undefined}
      {...(dragEnabled ? listeners : {})}
      // **Taken back off.** `@dnd-kit` puts `aria-roledescription="sortable"`
      // on every handle; the donor's tiles carry none, so a screen reader
      // there reads "button" and a position and nothing else. Renaming the
      // role would be a change to what the control announces itself as, which
      // is exactly the kind of "improvement" the parity floor rules out.
      aria-roledescription={undefined}
      // **And the same for the two states `useDraggable` adds.** It sets
      // `aria-pressed` on the tile it is carrying and `aria-disabled` on every
      // tile always (`core.esm.js:3406,3430-3438`); rbd's handle props are
      // exactly `tabIndex`, `role`, `aria-describedby`, `draggable` and
      // `onDragStart` (`draggable.jsx:105-113`) and carry neither, so a reader
      // in modsy hears "button" where these would make it "button, pressed".
      aria-pressed={undefined}
      aria-disabled={undefined}
      // rbd's own `dragHandleProps`, which `@dnd-kit` has no equivalent for.
      // A thumbnail is an `<img>` and an `<img>` is natively draggable, so a
      // press that moves can start Chromium's drag beside the reorder — and
      // its `dragover` reaches the box's file-drop handlers and tints the box
      // mid-gesture. The `dragstart` bubbles from the picture to here, which
      // is where the donor stops it too.
      draggable={false}
      onDragStart={(event) => event.preventDefault()}
      style={{
        transform:
          heldAt !== undefined
            ? `translate3d(${Math.round(heldAt)}px, 0px, 0)`
            : transform
              ? `translate3d(${Math.round(transform.x)}px, ${Math.round(transform.y)}px, 0)`
              : strandedBy
                ? `translate3d(${Math.round(strandedBy)}px, 0px, 0)`
                : undefined,
        transition: heldAt === undefined ? transition : heldOver,
        // The gap. Not `visibility`, because the drop animation's own side
        // effect holds this same property at 0 for the length of the settle so
        // the landing slot stays empty until the tile arrives.
        opacity: isDragging || heldHidden ? 0 : undefined,
      }}
      className={cn(
        thumbnailFrame(index === 0),
        // **PB-1.** rbd puts `cursor: grab` on every handle and `grabbing` on
        // the body for the length of a drag (`get-styles.js:75-78,87,149-151`).
        // The break is narrower than the ruling reads: modsy writes that
        // resting rule into a stylesheet it only populates after the first
        // drag, so a cold page shows a plain arrow over a strip that reorders.
        // Atlas applies the donor's own rule from the first paint.
        //
        // Off again for the length of a cancel's snap back — see
        // `cancelSettling`, which quotes rbd's own reason for the gap.
        dragEnabled && !cancelSettling && "cursor-grab",
        dragInFlight && "pointer-events-none",
      )}
    >
      <ThumbnailBody
        image={image}
        index={index}
        name={name}
        total={total}
        disabled={disabled}
        onRemove={onRemove}
        onMove={onMove}
      />
    </figure>
  );
}

export interface ImageUploadFieldProps {
  label: string;
  value: readonly UploadedImage[];
  onChange: (next: UploadedImage[]) => void;
  /** Hands the picked files to the caller, which uploads and appends them. */
  onFilesPicked: (files: File[]) => void;
  /** Removing is the caller's too — it may have an in-flight upload to forget. */
  onRemove: (index: number) => void;
  limit?: number;
  /**
   * Files the caller has taken but not yet handed back as rows.
   *
   * The donor's cap is `currentLimit = limit - value.length` fed to the
   * dropzone as `maxFiles` (`MultipleImageUploadField.tsx:121,132`), and rows
   * arrive one at a time as each
   * upload's turn comes — so the row count alone counts a five-file batch as
   * one for as long as its first file is on the wire, and a second pick made in
   * that window is measured against a cap that has not caught up. The label
   * promises six.
   */
  queued?: number;
  disabled?: boolean;
  /**
   * How far the upload in hand has got, 0-100. The donor's single number, and
   * on its own the whole gate: `showProgress = progress > 0`
   * (`MultipleImageUploadField.tsx:75`) is what mounts the bar, so there is no
   * second "uploading" flag to keep in step with it — one that said yes while
   * the number said nothing would hold an empty track in the box for the whole
   * gap between the picker closing and the first byte.
   */
  progress?: number;
  /** Said under the field, in the donor's own words. */
  error?: string;
  className?: string;
  "data-testid"?: string;
}

export function ImageUploadField({
  label,
  value,
  onChange,
  onFilesPicked,
  onRemove,
  limit = DEFAULT_IMAGE_LIMIT,
  queued = 0,
  disabled = false,
  progress = 0,
  error,
  className,
  "data-testid": testId,
}: ImageUploadFieldProps) {
  const inputId = useId();
  const messageId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  /** The strip itself — the donor's one droppable, and the area a drop needs. */
  const stripRef = useRef<HTMLDivElement>(null);
  /**
   * Where the lifted tile is rendered: `<body>`, once there is a document to
   * put it in.
   *
   * **A `position: fixed` tile is only positioned against the viewport while
   * nothing above it is transformed.** This field's other consumer is the
   * Request Item dialog, and the dialog shell centres itself with
   * `-translate-x-1/2 -translate-y-1/2` (`app-dialog.tsx:186`) — which makes
   * the dialog the containing block for every fixed descendant, so an overlay
   * rendered where it is declared would be laid out from the dialog's corner
   * and the tile would fly hundreds of pixels away from the pointer carrying
   * it. rbd never meets this because it lifts the tile in place, and modsy's
   * strip has no transformed ancestor; `renderClone` into a portal is the
   * escape hatch rbd offers for exactly this case.
   *
   * Client-only, and `useSyncExternalStore` is what states that to React
   * rather than to the bundle: the form is server-rendered, the server
   * renderer refuses portals outright, and `typeof document` cannot tell the
   * two passes apart in a suite that renders to a string *in a browser*
   * (`@/test/ssr-hydration`, which the row-failure cases here use). The
   * server snapshot is `null`, so the pass that has no DOM renders no portal
   * and hydration has nothing to reconcile — the overlay holds nothing until
   * a drag starts either way.
   */
  const liftedTileHome = useSyncExternalStore(
    subscribeToNothing,
    () => document.body,
    () => null,
  );
  const [dragActive, setDragActive] = useState(false);
  /** The tile under the pointer, or `null` when nothing is lifted. */
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  /**
   * Escape, rather than a release. rbd multiplies the settle by 0.6 for a
   * cancel (`get-drop-duration.js`), so the two endings are different lengths
   * and the shorter one reads as a snap back.
   *
   * Set in the same handler as `activeId`, which is what makes it in time:
   * React applies both in one pass, so the render that takes the overlay down
   * — the render whose `dropAnimation` the settle is run with — already knows
   * which ending this is.
   */
  const [cancelled, setCancelled] = useState(false);
  /**
   * Whether a cancel is still settling — true from Escape until the tile
   * lands back on its slot.
   *
   * rbd gives the handles `cursor: grab` in its `resting` and `dropAnimating`
   * states and **deliberately gives the `userCancel` state no cursor rule at
   * all**, with the reason written into the stylesheet: "Not applying grab
   * cursor during a user cancel as it is not possible for users to reorder
   * items during a cancel" (`get-styles.js:75-92`). So a cancelling donor
   * strip shows the plain arrow for the length of the snap back, and
   * `cancelled` on its own cannot say that — it stays true after the settle,
   * until the next lift.
   */
  const [cancelSettling, setCancelSettling] = useState(false);
  /**
   * Whether the tile in the air was lifted with the space bar.
   *
   * The two gestures end differently in the donor: a pointer drag is over
   * when the button comes up, while a keyboard drag has no held button and is
   * instead called off by any mouse or wheel activity at all.
   */
  const [keyboardDrag, setKeyboardDrag] = useState(false);
  /**
   * Whether the first `onDragOver` since the lift is still to come.
   *
   * `@dnd-kit` reports the tile as being over *itself* the instant a drag
   * starts, so the lift announcement is overwritten by "moved from position 3
   * to position 3" before a screen reader can finish it. rbd fires
   * `onDragStart` and then `onDragUpdate` only when the impact *changes*, so
   * the donor says nothing further until the tile is somewhere new — and it
   * does say "from position 3 to position 3" if you wander off and come back,
   * which is why this suppresses the first one rather than every one.
   */
  const stillOnItsOwnSlot = useRef(false);
  /**
   * The slot the drag last settled on, and with it which tiles are currently
   * shoved aside.
   *
   * rbd carries the previous frame's displacement groups into the next
   * (`get-drag-impact/index.js`'s `last`) because its swap boundary depends
   * on them. On one home list of same-width tiles the whole of that state is
   * the destination index, so that is what is kept. `null` until the first
   * move of a drag, which reads as "still on its own slot".
   */
  const settledOver = useRef<number | null>(null);
  /**
   * How far every tile *ahead* of the lifted one has closed up while the drag
   * is over no droppable at all, in pixels — 0 whenever it is over one.
   *
   * rbd names that state and answers it differently from every other: its
   * `getDragImpact` returns `noImpact` when the drag is over no droppable
   * (`no-impact.js`), displacing *nothing*. With the lifted tile out of the
   * flow, what a donor user sees is the bare collapse — the gap closing up
   * entirely behind the tile they have carried away.
   *
   * It cannot be said through the sorting strategy, which is where every
   * other displacement here lives: `useSortable` skips the strategy
   * altogether once `overIndex` is -1 (`sortable.esm.js:507,511`), so a
   * tile with no destination gets no transform at all. Hence the collapse is
   * applied from out here, on top of the nothing the strategy returns.
   */
  const [stranded, setStranded] = useState(0);
  /** The gutter and the collapse, measured at the lift while the row rests. */
  const liftGeometry = useRef({ gutter: 0, collapse: 0 });
  /**
   * The arrangement the strip holds while the tile flies to its slot, and the
   * reorder that lands with it.
   *
   * **The donor does not reorder at release.** rbd fires `onDragEnd` — the
   * responder whose `handleDragEnd` writes the new order and the new hero
   * (`MultipleImageUploadField.tsx:106-118`) — on `DROP_COMPLETE`, and
   * `DROP_COMPLETE` is dispatched only once the drop animation reports
   * finished (`responders-middleware.js:46-54`,
   * `drop-animation-finish-middleware.js:10-20`). For the whole settle the
   * strip keeps showing the drag's last impact
   * (`connected-draggable.js:308-320`), and the list changes underneath it at
   * the very end. Committing at release instead moves three things ~330ms
   * early, all of them visible: the 2px hero border jumps to the new first
   * tile, that tile grows 99→103px and shoves everything after it 4px, and
   * the trailing tiles un-collapse by a gutter — all while the tile is still
   * in the air.
   *
   * A cancel, and a release with no slot under it, hold a different
   * arrangement: rbd swaps the impact back to the lift's own and marks it
   * `forceShouldAnimate` (`get-drop-impact.js:38-53`), so the tiles *slide*
   * home over the sibling curve while the tile flies, and only reach true
   * rest when the settle ends.
   */
  const [held, setHeld] = useState<{
    /** One x-offset per tile, in the order the strip is still rendering. */
    at: number[];
    /** The curve they take to get there — absent when they are already in it. */
    over: string | undefined;
    /** The tile in the air: its slot stays empty until the settle finishes. */
    flying: number;
  } | null>(null);
  /** The reorder waiting on the settle, read from inside the animation. */
  const landing = useRef<{ from: number; to: number } | null>(null);
  /**
   * The live region, driven here rather than by `@dnd-kit`.
   *
   * rbd speaks all four of its sentences through one region, and the last of
   * them is **not** said at the release: `publisher.drop(result)` — the call
   * that runs `messagePreset.onDragEnd` (`publisher.js:210-226`) — hangs off
   * `DROP_COMPLETE`, which arrives only once the settle has finished
   * (`responders-middleware.js:44-54`, `drop-animation-finish-middleware.js:10-20`).
   * `@dnd-kit` announces from its monitor instead, so its drop sentence beats
   * the tile to the ground by the length of the settle and a screen-reader
   * user is told the result of a move still in the air.
   *
   * There is no way to delay `@dnd-kit`'s own announcement — the `announce`
   * its `Accessibility` component holds is internal (`core.esm.js:86-171`) —
   * so the four sentences are said from here and `@dnd-kit`'s callbacks are
   * silenced. Its region stays mounted and stays empty; its instructions node
   * is left alone, because the tile's `aria-describedby` points at it.
   */
  const [announcement, setAnnouncement] = useState("");
  /** What the drop owes the region, spoken when the tile lands — see `land`. */
  const owedSentence = useRef<string | null>(null);

  /**
   * **Held by identity, not rebuilt each render.** `useSortable` reads
   * `items !== previous.current.items` as "the list changed" and answers it
   * by dropping the out-of-the-way transition for a frame
   * (`sortable.esm.js:604-609`) — so a fresh array on every render makes any
   * re-render during a drag, from any state at all, silently cancel the
   * sibling curve. It was this field's live region that found it.
   *
   * The ids are row keys (`dragIdOf`), so the array's *contents* only change
   * when a row is added or removed: an upload landing mid-drag rewrites an
   * image's id and re-renders the strip without disturbing the curve, and
   * memoising on `value` is enough to keep the identity stable across it.
   */
  const sortableIds = useMemo(() => value.map(dragIdOf), [value]);
  /** The held offset of the tile in the air — the settle's own target. */
  const flyingTo = useRef(0);
  /**
   * **Which settle is still allowed to land.**
   *
   * rbd lets a second tile be lifted while the first is still coming down
   * (`get-styles.js:88-89`), and `startDrag` honours that by paying the first
   * drop what it is owed before the new one begins. But the first settle's
   * own callback is still out there: `dragOverlay.node.animate(...)` was
   * started in an earlier render, so its `onfinish` closes over *that*
   * render's `land` and `value`. Left alone it fires mid-second-drag, reads
   * `landing.current` — by then the **second** drop's indices — and splices
   * them into the list as it was before the first drop landed, so the first
   * reorder disappears and the second is applied to the wrong arrangement.
   *
   * A settle takes a ticket on the way out and only lands if the ticket is
   * still the current one. Bumped by `startDrag` as well, so paying a drop
   * early also disowns the animation that was going to pay it.
   */
  const settleTicket = useRef(0);
  /**
   * The settle currently in the air, so a new lift can cut it short.
   *
   * Retiring the ticket alone is not enough. `AnimationManager` holds its
   * clone of the overlay until the promise `settleTheDrop` returned resolves
   * (`core.esm.js:3604-3607`), and while it is holding one it runs no drop
   * animation for the next drag at all — so the second drop would never
   * reach `settleTheDrop`, and the reorder it made would never be written.
   * Cancelling the animation fires `oncancel`, which resolves that promise
   * and hands the manager back.
   */
  const settlingNow = useRef<Animation | null>(null);
  /**
   * The list as it is *now*, for the writes that happen a whole settle after
   * the render that scheduled them.
   *
   * `move` splices `value`, and between the release and the landing 330–550ms
   * later the strip can have taken writes from outside the drag — an upload
   * finishing is the everyday one (`item-images-uploader.ts:186-193`).
   * Splicing the closure's copy would hand those writes straight back.
   */
  const latestValue = useRef(value);
  useEffect(() => {
    latestValue.current = value;
  }, [value]);

  // The donor's pointer and keyboard sensors (`dnd-sensors.ts`), walking
  // left and right only — see `horizontalOnlyCoordinates`.
  const sensors = useDonorSensors(horizontalOnlyCoordinates);

  const room = limit - value.length - queued;
  /**
   * No room left, so the target goes — the donor's `value?.length < limit`
   * (`MultipleImageUploadField.tsx:183`) unmounting its `Grid item xs`.
   *
   * **It reads `room`, not the rows.** Counting rows alone kept the control
   * visible for as long as a cap-filling batch was still arriving, which sounds
   * gentler and produces a state the source cannot: a target sitting there
   * focusable and clickable while refusing every single file with a modal. In
   * modsy a visible target always means at least one more file is welcome —
   * `currentLimit >= 1` is what keeps it mounted — and that meaning is the part
   * worth holding on to. The cost is that the control leaves at the drop rather
   * than as the last thumbnail lands; it comes back the moment a row is
   * removed.
   */
  const full = room <= 0;
  /**
   * The last byte is sent.
   *
   * The donor's bar stops being a measurement at exactly this point and becomes
   * a "still working" animation — `variant={progress === 100 ? 'indeterminate'
   * : 'determinate'}` (`MultipleImageUploadField.tsx:234`). It reads as the
   * hand-off between the browser finishing the PUT and the app finishing with
   * the response.
   */
  const finished = progress >= 100;

  /**
   * The donor's `onDropRejected`, verbatim in effect: anything that is not a
   * JPEG or PNG is refused with one message covering the whole drop, and the
   * acceptable files in the same drop still go up.
   *
   * **One alert per drop, however many ways it is wrong.** react-dropzone
   * gathers every rejection and calls `onDropRejected(rejections, event)` once
   * — a drop that is both wrong-typed and over the cap raises a single dialog
   * in the donor, so raising a second one here would be two modals to dismiss
   * where modsy has one.
   */
  function receiveFiles(files: File[]) {
    if (disabled || files.length === 0) return;
    const ok = files.filter((file) => (DONOR_IMAGE_TYPES as readonly string[]).includes(file.type));
    // The donor's dropzone carries `maxFiles: currentLimit`
    // (`MultipleImageUploadField.tsx:121,132`) — the same arithmetic as `room`
    // above, minus the queued tail — and react-dropzone refuses the **whole**
    // batch when a drop exceeds it (`react-dropzone/src/index.js:694-700`): every
    // file lands in `fileRejections` as `too-many-files`, `onDrop` receives
    // nothing, and `onDropRejected` fires the one alert the field has. Taking
    // the first file and discarding the rest would be quieter and worse — the
    // typist watches one thumbnail appear and has no reason to think the others
    // did not.
    const overCap = ok.length > Math.max(room, 0);
    if (overCap || ok.length !== files.length) window.alert(DONOR_WRONG_TYPE_MESSAGE);
    if (overCap || ok.length === 0) return;
    onFilesPicked(ok);
  }

  function move(from: number, to: number) {
    // `latestValue`, not `value`: `land` calls this at the end of a settle,
    // and the closure it runs in can be a third of a second old.
    const current = latestValue.current;
    if (to < 0 || to >= current.length || from === to) return;
    const next = [...current];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved!);
    onChange(next);
  }

  const indexOf = (id: UniqueIdentifier) => value.findIndex((image) => dragIdOf(image) === id);
  const nameAt = (index: number) => {
    const image = value[index];
    return image ? nameOf(image.fileName, index) : "";
  };

  const activeIndex = activeId === null ? -1 : indexOf(activeId);
  const activeImage = activeIndex === -1 ? undefined : value[activeIndex];
  /**
   * **One image cannot be reordered, and neither can a field mid-save — both
   * named parity breaks, PB-2 and PB-3.**
   *
   * The donor does neither: its `<Draggable>` takes only `key`, `draggableId`
   * and `index` (`MultipleImageUploadField.tsx:154`), never `isDragDisabled`,
   * so every modsy tile is a live drag handle including the only one on a
   * one-image strip. PB-2 is the ruling that a one-tile reorder is a gesture
   * with nowhere to go. The `disabled` half is the same named break as the ✕ above
   * (ruling D8 for #411): modsy leaves the strip draggable while a save is in
   * the air and then posts the order it captured at submit.
   */
  const dragEnabled = !disabled && value.length > 1;

  /**
   * rbd's default announcements, with the image's name where it says "the
   * item" — **PB-4**, the one deliberate wording change.
   *
   * The donor reads "You have lifted an item in position 1" because its tiles
   * have nothing to be called: `alt=''` on the picture and no label on the
   * handle (`image-card.tsx:53`). Atlas already names every other control in
   * this field after the file (`Move front.jpg earlier`), and a strip of four
   * identical "an item" sentences is the state that made naming them worth
   * doing. `nameOf` supplies `Image 3` for the migrated rows whose `file_name`
   * is empty, exactly as it does for the buttons.
   *
   * Positions are 1-based and counted against the list as it stood at the
   * lift, which is rbd's arithmetic: `value` does not change until the drop
   * commits, so `indexOf` still reads the starting order inside every one of
   * these.
   */
  const sentences = {
    lifted: (from: number) => `You have lifted ${nameAt(from)} in position ${from + 1}`,
    nowhere: "You are over an area that cannot be dropped on",
    moved: (from: number, to: number) =>
      `You have moved ${nameAt(from)} from position ${from + 1} to position ${to + 1}`,
    dropped: (from: number, to: number) => {
      const name = nameAt(from);
      return to === -1
        ? `${name} has been dropped while not over a drop area. ${name} has returned to its starting position of ${from + 1}`
        : `You have dropped ${name}. You have moved ${name} from position ${from + 1} to position ${to + 1}`;
    },
    cancelled: (from: number) =>
      `Movement cancelled. ${nameAt(from)} has returned to its starting position of ${from + 1}`,
  };

  /**
   * Which tile the drag is over: rbd's droppable test, then rbd's reorder
   * rule. Neither is `closestCenter`.
   *
   * **The droppable test** (`get-droppable-over.js:17-23,95-133`). The donor
   * wraps the strip in a `<Droppable>` and a release outside it has no
   * destination at all — "The item has been dropped while not over a drop
   * area" — so some gate is needed, because `closestCenter` always names a
   * winner however far the pointer has gone, and without one letting go over
   * the title field silently reorders the images. But rbd's gate is *overlap*
   * plus a cross-axis test, not centre-in-box: on a horizontal list the tile
   * can hang half out past either end of the strip and the drop still lands.
   * Requiring the centre was ~49.5px tighter at each end than the donor.
   *
   * **The reorder rule** (`get-reorder-impact.js:74-116`). rbd swaps when the
   * dragged tile's *leading edge* passes a resting tile's centre, and swaps
   * back when its *trailing edge* does — an edge against a centre, where
   * `closestCenter` puts a centre against a centre. With 99px tiles on a
   * 111.8px pitch that is half a gutter, 6.4px, of difference in both
   * directions. The two `isDisplaced` branches are rbd's hysteresis: the
   * boundary moves with whether the tile has already been pushed aside, which
   * is what stops a swap flickering on alternate frames. They collapse to the
   * same point only because every tile here is the same width.
   *
   * **The row comes first.** rbd's scan walks the whole home list, because
   * modsy's strip never wraps; Atlas's does at four of six, so the release
   * point resolves a row before any edge is compared — owner ruling D7. The
   * comment on `rows` below has the reasoning.
   *
   * The strip element is read live rather than taken from
   * `args.droppableRects`, whose union is the tiles and not the gutters
   * between them. It is the same element the donor makes droppable. The tile
   * rects *are* taken from there, and must be the resting ones — hence the
   * explicit `measuring` on the context below.
   */
  const donorReorder: CollisionDetection = (args) => {
    const strip = stripRef.current?.getBoundingClientRect();
    if (!strip) return [];

    const target = args.collisionRect;
    // Off the strip, the donor's impact is `noImpact` — and `noImpact`'s
    // displaced set is *empty* (`no-impact.js:15-24`), not the last frame's.
    // So an excursion off the strip and back is judged from home, not from
    // whatever was shoved aside on the way out; forgetting that let the first
    // frame back name a slot a tile's width out.
    const leftTheStrip = () => {
      settledOver.current = null;
      return [];
    };

    const overlapsStrip =
      target.left < strip.right &&
      target.right > strip.left &&
      target.top < strip.bottom &&
      target.bottom > strip.top;
    if (!overlapsStrip) return leftTheStrip();

    const middle = strip.top + strip.height / 2;
    const centre = { x: target.left + target.width / 2, y: target.top + target.height / 2 };
    const centreInside =
      centre.x >= strip.left &&
      centre.x <= strip.right &&
      centre.y >= strip.top &&
      centre.y <= strip.bottom;
    const topInside = target.top >= strip.top && target.top <= strip.bottom;
    const bottomInside = target.bottom >= strip.top && target.bottom <= strip.bottom;
    const overTheStrip =
      centreInside ||
      // Taller than the strip and covering it outright.
      (!topInside && !bottomInside) ||
      // Otherwise the contained edge has to be past the halfway line.
      (topInside ? target.top < middle : target.bottom > middle);
    if (!overTheStrip) return leftTheStrip();

    const lifted = indexOf(args.active.id);
    const liftedRect = lifted === -1 ? undefined : args.droppableRects.get(args.active.id);
    if (!liftedRect) return [];

    // **Which row the tile was released over, before any edge is compared —
    // owner ruling D7 for #378, 2026-09-22.** `rowsOf` has the reasoning;
    // this is the pick.
    const rows = rowsOf(value, args.droppableRects);
    if (rows.length === 0) return [];

    const releasedAt = target.top + target.height / 2;
    // Zero gap is containment; otherwise the nearest row takes it, which is
    // what the gutter between two rows resolves to.
    const gapTo = (row: TileRow) => Math.max(row.top - releasedAt, releasedAt - row.bottom, 0);
    const row =
      rows.find((candidate) => gapTo(candidate) === 0) ??
      rows.reduce((nearest, candidate) =>
        gapTo(candidate) < gapTo(nearest) ? candidate : nearest,
      );

    // Which tiles are currently shoved aside — derivable from the last
    // destination, which is what rbd's `last` displacement groups amount to
    // on a single home list.
    const previous = settledOver.current ?? lifted;
    const isDisplaced = (index: number) => (index > lifted ? index > previous : index >= previous);

    let reachedAt: number | null = null;
    for (const { index, rect } of row.tiles) {
      if (index === lifted) continue;

      const tileCentre = rect.left + rect.width / 2;
      const displaced = isDisplaced(index);
      const reached =
        index > lifted
          ? displaced
            ? target.right <= tileCentre
            : target.left < tileCentre - liftedRect.width
          : displaced
            ? target.right <= tileCentre + liftedRect.width
            : target.left < tileCentre;

      if (reached) {
        reachedAt = index;
        break;
      }
    }

    // `atIndex` and `goAtEnd` (`get-reorder-impact.js:37-51`,
    // `calculate-reorder-impact.js:47-67`), read as an **insertion point** in
    // the list as it stands: a tile that has been reached is inserted before
    // it, and nothing reached means the tile has gone past the end of its
    // row, so it goes after that row's last tile. The `- 1` is the hole the
    // lifted tile leaves behind when it is ahead of where it is going.
    //
    // One consequence is worth naming rather than leaving to be found:
    // "after the last tile of row 1" and "the first slot of row 2" are the
    // same index, so a tile released past the right end of an earlier row
    // re-wraps and renders at the head of the next one. The list has one
    // order and the wrap is fixed, so no index means "the end of row 1" and
    // stays there. Pinned by the past-the-end case in the browser test.
    const insertAt = reachedAt ?? row.tiles.at(-1)!.index + 1;
    const destination = insertAt > lifted ? insertAt - 1 : insertAt;
    settledOver.current = destination;

    const landing = value[destination];
    return landing ? [{ id: dragIdOf(landing) }] : [];
  };

  useDonorDragBodyRules(activeId !== null);

  function startDrag(event: DragStartEvent) {
    // rbd lets a second tile be lifted while the first is still landing
    // (`get-styles.js:88-89`). The one it was landing on is owed its reorder
    // first — otherwise the new drag reads a list the old drop never reached.
    // Paying it here also retires its ticket: the animation still in the air
    // must not come back and pay this drag's reorder out of a stale list.
    // Cutting it short is the other half — see `settlingNow`.
    settleTicket.current += 1;
    settlingNow.current?.cancel();
    settlingNow.current = null;
    land();
    setCancelled(false);
    setCancelSettling(false);
    stillOnItsOwnSlot.current = true;
    settledOver.current = null;
    setStranded(0);
    liftGeometry.current = measureAtLift(stripRef.current, indexOf(event.active.id));
    setKeyboardDrag(event.activatorEvent instanceof KeyboardEvent);
    setActiveId(event.active.id);
    setAnnouncement(sentences.lifted(indexOf(event.active.id)));
  }

  /** The end of the settle: the reorder lands and the held arrangement goes. */
  function land() {
    const waiting = landing.current;
    const owed = owedSentence.current;
    landing.current = null;
    owedSentence.current = null;
    setHeld(null);
    // rbd's `DROP_COMPLETE` carries both: `publisher.flush(); next(action);
    // publisher.drop(result)` — the reorder and the sentence in one beat.
    if (owed !== null) setAnnouncement(owed);
    if (waiting) move(waiting.from, waiting.to);
  }

  /**
   * The arrangement rbd puts back when a drag ends with nothing under it —
   * the impact the lift itself made, which is the bare collapse and nothing
   * displaced — animated rather than jumped to (`get-drop-impact.js:38-53`).
   */
  function backToTheLiftsOwnImpact(lifted: number) {
    const closed = -liftGeometry.current.gutter;
    return value.map((_, index) => (index > lifted ? closed : 0));
  }

  // The donor's drag keys and the gestures that call a keyboard drag off —
  // shared with the sort rows (`dnd-sensors.ts`).
  useDonorDragKeys(activeId !== null, keyboardDrag);

  function endDrag({ active, over }: DragEndEvent) {
    const from = indexOf(active.id);
    const to = over ? indexOf(over.id) : -1;
    setActiveId(null);
    setStranded(0);
    owedSentence.current = sentences.dropped(from, to);

    if (to === -1) {
      // No droppable under the release: rbd returns the tile to its slot and
      // changes nothing (`drop-middleware.js`'s null-destination branch),
      // with the row sliding back to the lift's own impact as it goes.
      hold(backToTheLiftsOwnImpact(from), SIBLING_TRANSITION, from);
      return;
    }

    // A slot under the release: the row is already showing the arrangement
    // the drop makes, so it simply stops where it is — every tile's live
    // transform, frozen — and the reorder lands when the tile does. `from ===
    // to` is the release that travelled and came back: nothing to reorder,
    // but the same hold, because rbd holds its last impact either way.
    const frozen = Array.from(stripRef.current?.children ?? []).map(
      // Read back out of the tile's own inline transform, which this file is
      // the only writer of — the arrangement is already on the screen, so
      // there is nothing to recompute and nothing that can disagree with it.
      (tile) => new DOMMatrixReadOnly((tile as HTMLElement).style.transform).m41,
    );
    landing.current = from === to ? null : { from, to };
    // **The curve stays on.** rbd's out-of-the-way transition is declared in
    // all three of `dragging`, `dropAnimating` and `userCancel`
    // (`get-styles.js:103-115`) — it never comes off a sibling for a drop.
    // The frozen values *are* the targets each tile is already heading for,
    // so keeping the curve changes nothing except letting a tile still
    // mid-slide finish gliding rather than snapping the rest of the way.
    hold(frozen, SIBLING_TRANSITION, from);
  }

  function cancelDrag() {
    const from = activeIndex;
    setCancelled(true);
    setCancelSettling(true);
    setActiveId(null);
    setStranded(0);
    if (from === -1) return;
    owedSentence.current = sentences.cancelled(from);
    hold(backToTheLiftsOwnImpact(from), SIBLING_TRANSITION, from);
  }

  /** One writer for the held arrangement, so the settle's target rides along. */
  function hold(at: number[], over: string | undefined, flying: number) {
    flyingTo.current = at[flying] ?? 0;
    setHeld({ at, over, flying });
  }

  /**
   * The settle.
   *
   * Written out rather than handed to `@dnd-kit` as a `{ duration, easing }`
   * because the donor's duration is not a constant: it depends on how far the
   * tile still has to fly, and that distance is only known here, inside the
   * animation, as the gap between where the overlay is and where its slot
   * ended up. It is `@dnd-kit`'s own default animation otherwise — the two
   * keyframes, the `fill: forwards`, and the side effect holding the source
   * tile at `opacity: 0` until the overlay lands on it, which is what keeps
   * the slot from flickering full a frame early. The one addition is the
   * layer drop: rbd settles at 4500 rather than the 5000 it carries at
   * (`get-style.js:86-88`), leaving room for a second tile to be lifted over
   * one that has not finished landing.
   */
  const settleTheTile: DropAnimationFunction = ({ active, dragOverlay, transform }) => {
    // The slot measured off the node itself rather than from `active.rect`.
    // `@dnd-kit` measures a draggable with `getTransformAgnosticClientRect`
    // (`core.esm.js:2479-2481`), which reads the node as if it carried no
    // transform — and the slot this tile is flying to is *spelled* as a
    // transform now that the reorder waits for the settle, so the agnostic
    // rect points at the slot the tile was lifted out of instead.
    // The slot is where the tile's *held* offset puts it, not where the node
    // is standing. `active.rect` is `getTransformAgnosticClientRect`
    // (`core.esm.js:2479-2481`) — the node's layout position with its
    // transform read back out — and now that the reorder waits for the
    // settle, the landing slot is spelled entirely as that transform. Nor
    // will the node's live rect do: the tiles carry a 200ms transition, so
    // mid-move the rect is wherever the transition has got to.
    const slot = { left: active.rect.left + flyingTo.current, top: active.rect.top };
    const travel = {
      x: dragOverlay.rect.left - slot.left,
      y: dragOverlay.rect.top - slot.top,
    };
    const landed = { x: transform.x - travel.x, y: transform.y - travel.y };

    // **A drop with nowhere to travel is not animated at all.** rbd guards the
    // whole drop animation on `isAnimationRequired` — `!isEqual(current
    // offset, newHomeClientOffset) || combine` — and dispatches `completeDrop`
    // synchronously when it is false (`drop-middleware.js:121-131`). That
    // guard sits *in front of* `getDropDuration`, which has no other caller,
    // so the `distance <= 0` branch that reads like a 330ms floor is reachable
    // only through `combine` — a mode this field never uses.
    //
    // It is the keyboard gesture this decides. A keyboard lift takes the
    // tile's own border-box centre as the selection
    // (`use-sensor-marshal.js:313-314`), every arrow recomputes the selection
    // from the impact (`move-to-next-place/index.js:100-112`), and the drop
    // derives its home offset from that same impact by the same pure
    // composition (`get-new-home-client-offset.js:38-53`) — so the two are
    // equal and Space commits on the keystroke. Nothing would move during the
    // settle in any case; what it costs is the wait before the row re-flows
    // and before the drop is announced.
    //
    // Decided off the drag's *intended* offset, not off the measured rect
    // above. A keyboard move is transitioned (`transform 200ms`), and
    // `@dnd-kit` hands this function `getComputedStyle(node).transform`
    // (`core.esm.js:3785-3788`) — mid-slide that is the interpolated value,
    // so a rect-based comparison would report travel for a drop the donor
    // treats as standing still. The inline `style.transform` is the value
    // `PositionedOverlay` last wrote (`core.esm.js:3666`), which the running
    // transition never touches: the drag's offset, as state, against
    // `flyingTo` — the home offset the held impact implies. Those two are
    // exactly rbd's `current.client.offset` and `newHomeClientOffset`.
    const intended = new DOMMatrixReadOnly(dragOverlay.node.style.transform);
    if (intended.m41 === flyingTo.current && intended.m42 === 0) {
      setCancelSettling(false);
      land();
      return;
    }

    const slotOpacity = active.node.style.opacity;
    const carriedAt = dragOverlay.node.style.zIndex;
    active.node.style.opacity = "0";
    dragOverlay.node.style.zIndex = String(SETTLING_Z_INDEX);

    // **The flying tile stays inert for the settle — it is the *other* tiles
    // that come back.** `dropAnimating: grabCursor` on the drag-handle
    // selector (`get-styles.js:90`) is aimed at the resting handles, and
    // rbd's comment at `:89` says why: "it is fine for users to start
    // dragging another item when a drop animation is occurring". It cannot
    // reach the tile in the air, because `getDraggingStyle` writes
    // `pointerEvents: 'none'` *inline* on that node — unconditionally, under
    // the comment "Blocking any pointer events on the dragging or dropping
    // item" (`get-style.js:88-90`) — and `connected-draggable.js:132-175`
    // keeps the dropping tile mapped as `DRAGGING` for the whole
    // `DROP_ANIMATING` phase. An inline rule beats the sheet, and rbd notes
    // at `get-styles.js:63` that the cursor does not apply under it at all.
    //
    // A previous revision read the sheet without the inline rule and handed
    // the settling tile its pointer events and a `grab` cursor back. That
    // made it mask what it flew over and pop its own hover bar at the end of
    // every drop. The resting tiles' half — `dragInFlight` ending at the
    // release — is the part that was right, and stays.

    const settling = dragOverlay.node.animate(
      [
        { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` },
        { transform: `translate3d(${landed.x}px, ${landed.y}px, 0)` },
      ],
      {
        duration: settleOver(Math.hypot(travel.x, travel.y), cancelled),
        easing: DROP_EASING,
        fill: "forwards",
      },
    );

    const ticket = (settleTicket.current += 1);
    settlingNow.current = settling;

    return new Promise<void>((landedThere) => {
      const arrive = () => {
        active.node.style.opacity = slotOpacity;
        dragOverlay.node.style.zIndex = carriedAt;
        if (settlingNow.current === settling) settlingNow.current = null;
        // **Only the current settle lands.** A second lift retires this
        // ticket (`settleTicket`), and by then the drop this animation was
        // running for has already been paid by `startDrag`. Coming back to
        // call `land` anyway would spend the *next* drop's `landing.current`
        // through this closure's stale list. The promise is still resolved
        // either way — `AnimationManager` is holding a clone of the overlay
        // until it settles (`core.esm.js:3604-3607`).
        if (settleTicket.current !== ticket) return landedThere();
        setCancelSettling(false);
        // rbd's `DROP_COMPLETE`: the reorder is written and the impact
        // cleared in the same beat, at the end of the settle rather than at
        // the release that started it.
        land();
        landedThere();
      };
      settling.onfinish = arrive;
      // **A settle that is cancelled still has to arrive.** This promise is
      // what `AnimationManager` is waiting on before it drops the clone of
      // the overlay it is holding (`core.esm.js:3604-3607`), so a settle that
      // ends any way other than `finish` and never resolves leaves that clone
      // on the page and the reorder unwritten. `onfinish` alone covers the
      // ordinary end of a drop; this covers a settle interrupted by the node
      // going away underneath it.
      settling.oncancel = arrive;
    });
  };

  return (
    // The donor's `FormControl` — the label and the box are wrapped in a
    // `Stack spacing={1.375}` (11px), and the helper text is a *sibling of that
    // Stack* (`:42-55`), so the 11px never applies to the message — it hangs on
    // its own 3px margin instead.
    // `MuiFormControl.styleOverrides.root` is one of the eight components the
    // donor's theme touches, and it paints the root `#ffffff` (`theme.ts:381`)
    // — so the label row and the helper-text row sit on white too, not only the
    // box. Invisible on a white host; visible the moment one is tinted.
    <div className={cn("bg-white", className)} data-testid={testId}>
      {/* **Around the whole field, not around the strip.** `@dnd-kit` renders
      two hidden nodes of its own for the screen-reader announcements — a
      `display: none` description and a `position: fixed` live region
      (`@dnd-kit/accessibility`) — inline, wherever the context sits. Inside
      the thumbnail row they would be flex children; here they are two
      out-of-flow nodes in a plain block, which is also where the donor puts
      them (rbd appends both to `<body>`). */}
      <DndContext
        sensors={sensors}
        collisionDetection={donorReorder}
        // **Explicit because both the collision rule and the sort strategy
        // read these rects as *resting* geometry.** `WhileDragging` is
        // `@dnd-kit`'s own default — it measures once at the lift and not
        // again — but the whole of `donorReorder`'s arithmetic is edges
        // against resting centres, and a strategy that re-measured mid-drag
        // would feed it tiles already carrying their own transforms.
        measuring={{ droppable: { strategy: MeasuringStrategy.WhileDragging } }}
        accessibility={{
          // Silenced, not replaced: its region stays mounted and stays empty,
          // and the sentences are said through the one below — see
          // `announcement` for why the drop cannot be spoken from here.
          announcements: SAID_ELSEWHERE,
          screenReaderInstructions: { draggable: DRAG_HANDLE_INSTRUCTIONS },
        }}
        onDragStart={startDrag}
        onDragOver={({ active, over }) => {
          setStranded(over ? 0 : liftGeometry.current.collapse);
          // rbd announces a move only when the impact actually changes, so
          // the first `onDragOver` of a lift — which reports the tile over
          // its own slot — says nothing and leaves the lift sentence
          // standing.
          const sinceTheLift = stillOnItsOwnSlot.current;
          stillOnItsOwnSlot.current = false;
          if (!over) return setAnnouncement(sentences.nowhere);
          const from = indexOf(active.id);
          const to = indexOf(over.id);
          if (sinceTheLift && to === from) return;
          setAnnouncement(sentences.moved(from, to));
        }}
        onDragEnd={endDrag}
        onDragCancel={cancelDrag}
      >
        {/* The live region, rbd's single one. It sits before `@dnd-kit`'s own
        — which renders after this context's children (`core.esm.js:3357-3363`)
        and never has anything in it — so the first `role="status"` in the
        field is the one that speaks. Same markup `@dnd-kit` uses, since the
        enumeration found the donor's region and its own already agree:
        `assertive` and atomic. */}
        <div
          role="status"
          aria-live="assertive"
          aria-atomic
          className="fixed top-0 left-0 m-[-1px] h-px w-px overflow-hidden border-0 p-0 whitespace-nowrap [clip-path:inset(100%)] [clip:rect(0_0_0_0)]"
        >
          {announcement}
        </div>
        <div className="flex flex-col gap-[11px]">
          {/* **No letter-spacing on any role here, the error caption below
        included.** MUI's variants carry `letterSpacing` only when the family is
        its own Roboto stack — `buildVariant` guards the whole property on
        `fontFamily === defaultFontFamily` (`createTypography.js:49-58`) — and
        the donor sets Inter (`theme.ts:123`), so every variant computes to
        `normal` in modsy. */}
          <label htmlFor={inputId} className="text-lennar-ink text-base">
            {label}
          </label>

          <div
            data-slot="image-upload-box"
            // The donor's `Grid container` box.
            //
            // **Atlas accepts a drop anywhere in the box; the donor only accepts
            // one on the button.** Its `getRootProps()` is spread on the
            // drop-target `ButtonBase` (`MultipleImageUploadField.tsx:185`), so
            // dropping onto the thumbnails, onto the progress bar, or into the
            // 16px padding does nothing there and shows no feedback — while its
            // *hover* grey covers the whole box, which is what makes the whole box
            // look droppable. Widening the target is additive rather than a loss,
            // so it stays — a divergence, named, and then **ruled**: it is the
            // first of the five owner-licensed breaks on #411's rulings record.
            onDragOver={(event) => {
              if (disabled || full) return;
              event.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={() => setDragActive(false)}
            onDrop={(event) => {
              if (disabled || full) return;
              event.preventDefault();
              setDragActive(false);
              receiveFiles([...event.dataTransfer.files]);
            }}
            className={cn(
              "border-lennar-divider flex flex-wrap items-stretch gap-[12.8px] rounded-[4px] border bg-white p-4",
              "hover:bg-lennar-action-hover",
              dragActive && "bg-lennar-action-hover",
              // **A ruled divergence** (#411): the donor's border is hardcoded
              // to `divider` with no error branch (`:141-143`), and its
              // `FormControl error` reaches nothing but the helper text. Reddening
              // the box is additive — the message alone is easy to miss under a
              // wide field — and the owner ruled it covered by #300's ruling
              // D2 rather than as a fresh break.
              error && "border-lennar-error",
            )}
          >
            {/* **The thumbnails are their own block.** The donor's box is a
          `Grid container` of exactly two items — the droppable, which nests its
          own `Grid container spacing={1.6}` around the tiles, and the target's
          `Grid item xs` (`:151,181,183,240-270`). Two consequences a single flat
          wrap gets wrong: the tiles wrap *among themselves* so the target never
          drops onto a line of its own, and the droppable renders even with
          nothing in it, so an empty box is a zero-width item plus the 12.8px
          gutter — which is the inset the empty-state target starts at. */}
            <div
              ref={stripRef}
              data-slot="image-upload-thumbnails"
              // `overflow-anchor: none` is rbd's `always` rule for the
              // droppable itself (`get-styles.js:120-126`) — not only for the
              // length of a drag, the way the body rule is.
              className="flex flex-wrap gap-[12.8px] [overflow-anchor:none]"
            >
              <SortableContext items={sortableIds} strategy={donorDisplacement}>
                {value.map((image, index) => (
                  <SortableThumbnail
                    key={image.key}
                    image={image}
                    index={index}
                    name={nameOf(image.fileName, index)}
                    total={value.length}
                    disabled={disabled}
                    dragEnabled={dragEnabled}
                    dragInFlight={activeId !== null}
                    cancelSettling={cancelSettling}
                    strandedBy={
                      stranded > 0 && activeId !== null && index > indexOf(activeId) ? -stranded : 0
                    }
                    heldAt={held?.at[index]}
                    heldOver={held?.over}
                    heldHidden={held?.flying === index}
                    onRemove={onRemove}
                    onMove={move}
                  />
                ))}
              </SortableContext>
            </div>

            {!full ? (
              // The donor's `Grid item xs` — `flexBasis: 0; flexGrow: 1`
              // (`gridGenerator.js:22-28`) over the `minWidth: 0` every Grid
              // carries (`:151-166`) — wrapping the button
              // rather than being it. The two-element shape is load-bearing: a
              // flex line is measured on each item's base size *plus its padding*,
              // so putting the button's 16px inset on the item itself made the
              // target wrap onto a row of its own while 26px of room still sat
              // beside the thumbnails. The donor's padding is on the `ButtonBase`
              // inside (`:189`), where it costs the line nothing.
              <div className="min-w-0 shrink grow basis-0">
                <button
                  type="button"
                  disabled={disabled}
                  aria-label={label}
                  data-slot="image-upload-target"
                  onClick={() => inputRef.current?.click()}
                  className={cn(
                    // No gap: `Typography` zeroes the `<p>` margins and the donor's
                    // `ButtonBase` sets none, so the three children stack flush
                    // (`:202-230`). No disabled fade either — `ButtonBase` carries no
                    // disabled opacity, and the theme's `opacity: 0.5` rule is scoped
                    // to `MuiButton` (`ButtonBase.js:74-78`, `theme.ts:328-329`), so
                    // the donor's target looks the same disabled as enabled.
                    //
                    // `w-full` is the donor's `...(!hasImages && { width: 1 })`
                    // and only that: beside thumbnails the `<button>` shrinks to
                    // its content inside the growing item, `display: flex` or not
                    // (measured live, #657).
                    "flex h-[75px] flex-col items-center justify-center p-4",
                    value.length === 0 && "w-full",
                    // The `ButtonBase` reset this control would have inherited
                    // had it been one. No `text-align` in it, deliberately: MUI
                    // declares none, so a native `<button>` keeps the UA sheet's
                    // `center` on both sides — which is why a wrapped headline or
                    // hint centres in modsy rather than hanging left.
                    MUI_BUTTON_BASE_RESET,
                  )}
                >
                  {/* A MUI `Avatar` at its default 40px on `blueGrey[50]` — imported
              straight from `@mui/material/colors` rather than through a theme
              token (`:9,202`), which is why it is the one MUI-palette ground in
              the app. The glyph is `UploadFile` at the default `fontSize='medium'`
              = `pxToRem(24)` in `primary.main`; `FileUp` is lucide's page-with-an-
              up-arrow, the same figure as MUI's. */}
                  <span
                    data-slot="image-upload-target-icon"
                    className="bg-lennar-blue-grey-50 flex size-10 shrink-0 items-center justify-center rounded-full"
                  >
                    <FileUp className="text-primary size-6" />
                  </span>
                  {/* `variant='subtitle1'` — 16px at weight 500, line-height 1.5
              (`theme.ts:165-169`) — with `component='p'` so it is not an `<h6>`.
              Both states share the shell; only the words change (`:205-224`). */}
                  <span
                    data-slot="image-upload-target-headline"
                    className="text-lennar-ink text-base leading-normal font-medium"
                  >
                    {value.length === 0 ? (
                      <>
                        {/* A `MuiLink` with every prop left undefined, so MUI's own
                    defaults are the spec: `underline='always'` rules the
                    decoration at `alpha(primary.main, 0.4)`, not at the ink, and
                    its `&:hover { textDecorationColor: 'inherit' }` — a rule that
                    outranks the resting colour on specificity — resolves to
                    `currentcolor`, so the underline darkens to the link's own
                    primary under the pointer (`Link.js:80-112`). It is a `<span>`
                    with no `href` in the donor too — it only looks like a link,
                    and it **weighs what the sentence around it weighs**:
                    `variant` defaults to `'inherit'` (`:184`), which Typography
                    renders as `font: inherit` (`Typography.js:47-56`). */}
                        <span
                          data-slot="image-upload-target-link"
                          className="text-primary decoration-lennar-primary-underline hover:decoration-primary underline"
                        >
                          Click to upload
                        </span>{" "}
                        or drag and drop images
                      </>
                    ) : (
                      "Add Images"
                    )}
                  </span>
                  {/* `variant='subtitle1'` with `sx` overriding the size alone, so the
              hint keeps subtitle1's weight 500 and 1.5 line-height at 12px — 18px
              of box, not `caption`'s 400 (`:226-230`). */}
                  <span
                    data-slot="image-upload-target-hint"
                    className="text-lennar-ink-secondary text-xs leading-[1.5] font-medium"
                  >
                    {DONOR_IMAGE_TYPES_HINT}
                  </span>
                </button>
              </div>
            ) : null}

            {progress > 0 ? (
              // The donor's `LinearProgress` — now the shared Atlas component, since
              // its look is MUI's unoverridden default and that is the source spec
              // (`migration-guidelines.md`, "Shared donor components port as shared
              // Atlas components"). Driven by real bytes-sent from the PUT; it pulsed
              // here because `fetch` reports no progress, and ruling D3 for #300
              // moved the PUT to `XMLHttpRequest`, which does. One bar and one number
              // for the whole field, as the donor has — coherent because uploads run
              // one at a time (ruling D4).
              //
              // **Mounted on `progress > 0`, not on a separate flag** — the donor's
              // `showProgress = progress > 0` (`:75`) takes the bar out of the DOM
              // between uploads, so the box is shorter before bytes move and again
              // after they land.
              //
              // **`undefined` at 100 is the flip**, matching the donor's
              // `variant={progress === 100 ? 'indeterminate' : 'determinate'}`
              // (`:234`).
              <LinearProgress
                aria-label="Uploading images"
                {...(finished ? {} : { value: progress })}
              />
            ) : null}
          </div>
        </div>

        {/* Unmounted at the cap with the target it belongs to: the donor's
      `<input {...getInputProps()} />` lives *inside* the `ButtonBase`
      (`:200`), which the `value.length < limit` guard takes out — so at the cap
      the label has nothing to open, rather than a picker whose every selection
      meets `room === 0` and raises the refusal dialog. Ruling D6 for #300
      licenses the label opening the picker; it does not license it at the
      cap. */}
        {!full ? (
          <input
            ref={inputRef}
            id={inputId}
            type="file"
            multiple
            accept={DONOR_IMAGE_TYPES.join(",")}
            disabled={disabled}
            className="sr-only"
            // `getInputProps()` returns `tabIndex: -1` (react-dropzone
            // `src/index.js:966`): the picker is reached through the target, and the
            // hidden input is not a stop of its own on the way there.
            tabIndex={-1}
            aria-describedby={error ? messageId : undefined}
            onChange={(event) => {
              receiveFiles([...(event.target.files ?? [])]);
              // Cleared so picking the same file twice still fires a change.
              event.target.value = "";
            }}
          />
        ) : null}

        {error ? (
          // `FormHelperText`: `margin: 3px 0 0 0` and `text-align: left`, plus
          // **14px of horizontal margin**. That inset belongs to the `contained`
          // variant, and this is one: `contained` is `variant === 'filled' ||
          // 'outlined'` read off the `FormControl` context
          // (`FormHelperText.js:70-78,105`), and `FormControl` defaults
          // `variant='outlined'` (`FormControl.js:121`) — neither donor consumer
          // passes one. Type is `theme.typography.caption` at the donor's 12px
          // with MUI's own unoverridden weight 400 and line-height 1.66, in
          // `error.main` #ff1744. No tracking — see the label above.
          <p
            id={messageId}
            data-slot="image-upload-error"
            className={cn("text-lennar-error", MUI_FIELD_HELPER_TEXT)}
          >
            {error}
          </p>
        ) : null}

        {/* The tile under the pointer. Without it `@dnd-kit` animates the source
      node from its pre-drag *layout* rect, so a released tile jumps back to
      the slot it left and only then slides to its new one — the donor does
      neither. The overlay is a `position: fixed` copy that is already where
      the pointer is, which is what rbd's lifted tile is too (`z-index: 5000`,
      `position: fixed`, `pointer-events: none`, measured on item 2391). It
      departs from the ticket's stated Implementation Decision and is raised as
      such in the decision round. */}
        {liftedTileHome
          ? createPortal(
              <DragOverlay
                zIndex={LIFTED_Z_INDEX}
                // On the carrier rather than the figure inside it, because the
                // carrier is the box the pointer would otherwise land on. rbd blanks
                // it on the lifted tile itself (`get-styles.js:88`) — same element,
                // same result: the drag never hovers what it is passing over.
                //
                // **For the settle too, which is the donor's rule and not an
                // oversight.** `getDraggingStyle` writes `pointerEvents: 'none'`
                // inline on the dragged node unconditionally, and the dropping tile
                // is still mapped as `DRAGGING` (`get-style.js:88-90`,
                // `connected-draggable.js:132-175`) — so modsy's tile passes the
                // pointer through for the whole of its flight home. It is the
                // *resting* tiles that take their cursor back during `dropAnimating`.
                style={{ pointerEvents: "none" }}
                // A keyboard drag moves in steps, so the tile has to travel between
                // them; rbd animates that with the same curve the siblings shift on
                // (`animation.js`'s `outOfTheWay`). A pointer drag is not transitioned
                // at all — the tile is wherever the pointer is.
                transition={(activatorEvent) =>
                  activatorEvent instanceof KeyboardEvent
                    ? `transform ${SIBLING_SHIFT.duration}ms ${SIBLING_SHIFT.easing}`
                    : undefined
                }
                dropAnimation={settleTheTile}
              >
                {activeImage ? (
                  // Hidden from the accessibility tree: the source tile is still in
                  // the DOM, still focused, and still the thing the live region is
                  // talking about — this is its picture, not a second control.
                  <figure
                    aria-hidden
                    // Unreachable, and deliberately so: the carrier above is
                    // `pointer-events: none` for the drag *and* the settle, so
                    // the pointer never lands on this node and `<body>`'s
                    // `grabbing` is what the editor sees. rbd is in the same
                    // position — it declares a cursor on every handle and then
                    // notes that "the cursor will not apply when pointer-events
                    // is set to none" (`get-styles.js:63`).
                    className={cn(thumbnailFrame(activeIndex === 0), "m-0 cursor-grabbing")}
                  >
                    <ThumbnailBody
                      image={activeImage}
                      index={activeIndex}
                      name={nameAt(activeIndex)}
                      total={value.length}
                      disabled={disabled}
                      onRemove={onRemove}
                      onMove={move}
                    />
                  </figure>
                ) : null}
              </DragOverlay>,
              liftedTileHome,
            )
          : null}
      </DndContext>
    </div>
  );
}
